import { useState, useEffect } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How quickly can I get a provider?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Availability depends on your location and the service requested. Many jobs are accepted quickly by nearby providers."
          }
        },
        {
          "@type": "Question",
          "name": "Are providers verified?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. All providers are ID-verified, DBS checked where applicable, and fully insured."
          }
        },
        {
          "@type": "Question",
          "name": "Do I pay upfront?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You pay securely in the app. Payment is only taken once the job is completed."
          }
        },
        {
          "@type": "Question",
          "name": "Can I cancel a booking?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Cancellations are subject to our Cancellation & Refund Policy, depending on timing."
          }
        },
        {
          "@type": "Question",
          "name": "Are prices fixed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You'll see pricing clearly before booking. There are no hidden fees."
          }
        }
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      if (schemaScript.parentNode) {
        schemaScript.parentNode.removeChild(schemaScript);
      }
    };
  }, []);

  const faqs = [
    {
      question: 'How quickly can I get a provider?',
      answer: 'Availability depends on your location and the service requested. Many jobs are accepted quickly by nearby providers.'
    },
    {
      question: 'Are providers verified?',
      answer: 'Yes. All providers are ID-verified, DBS checked where applicable, and fully insured.'
    },
    {
      question: 'Do I pay upfront?',
      answer: 'You pay securely in the app. Payment is only taken once the job is completed.'
    },
    {
      question: 'Can I cancel a booking?',
      answer: 'Yes. Cancellations are subject to our Cancellation & Refund Policy, depending on timing.'
    },
    {
      question: 'Are prices fixed?',
      answer: 'You\'ll see pricing clearly before booking. There are no hidden fees.'
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">
            Everything you need to know
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl border-2 border-gray-100 hover:border-orange-500 overflow-hidden transition-all duration-500 animate-on-scroll"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-orange-50 transition-colors duration-300"
              >
                <span className="text-lg font-semibold text-gray-900 pr-4 group-hover:text-orange-500 transition-colors duration-300">
                  {faq.question}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-2xl text-orange-500 flex-shrink-0 transition-transform duration-500 ${
                    openIndex === index ? 'rotate-180' : 'rotate-0'
                  }`}
                ></i>
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100">
                  <p className="pt-4">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
